/**
 * loader 元数据字段读取工具。
 *
 * 设计说明：
 * 1. GLB parser JSON 中的 STEP、PMI、单位等字段来源多样，但读取规则应统一；
 * 2. 这里只处理纯 `Record<string, unknown>`，不依赖 three.js 或具体导入模型类型；
 * 3. 业务模块负责传入候选记录和字段名，避免同类字段读取逻辑散落在 loader 各处。
 */

/**
 * 解析有限数字。
 *
 * @param value 原始字段值。
 * @returns 有限数字；无法解析时返回 `undefined`。
 */
function parseFiniteMetadataNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value.trim())
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

/**
 * 遍历候选记录中的字段值。
 *
 * @param records 候选记录集合。
 * @param keys 候选字段名集合。
 * @param visitor 字段访问函数；返回 `undefined` 表示继续查找。
 * @returns 第一个有效访问结果。
 */
function visitMetadataFields<T>(
  records: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
  visitor: (value: unknown) => T | undefined,
): T | undefined {
  for (const record of records) {
    if (!record) {
      continue
    }
    for (const key of keys) {
      const result = visitor(record[key])
      if (result !== undefined) {
        return result
      }
    }
  }
  return undefined
}

/**
 * 读取字符串字段。
 *
 * @param records 候选记录集合。
 * @param keys 候选字段名集合。
 * @returns 第一个有效字符串。
 */
export function readStringMetadataValue(
  records: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
): string | undefined {
  return visitMetadataFields(records, keys, (value) => {
    if (typeof value !== 'string') {
      return undefined
    }
    const normalizedValue = value.trim()
    return normalizedValue.length > 0 ? normalizedValue : undefined
  })
}

/**
 * 读取有限数字字段。
 *
 * @param records 候选记录集合。
 * @param keys 候选字段名集合。
 * @returns 第一个有限数字。
 */
export function readNumberMetadataValue(
  records: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
): number | undefined {
  return visitMetadataFields(records, keys, parseFiniteMetadataNumber)
}

/**
 * 读取正数数字字段。
 *
 * @param records 候选记录集合。
 * @param keys 候选字段名集合。
 * @returns 第一个有效正数。
 */
export function readPositiveNumberMetadataValue(
  records: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
): number | undefined {
  return visitMetadataFields(records, keys, (value) => {
    const numberValue = parseFiniteMetadataNumber(value)
    return numberValue !== undefined && numberValue > 0 ? numberValue : undefined
  })
}
